
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import { v4 as uuid } from "uuid";
  import storage from "@/services/storage";

  import CREATE_DOCUMENT from "@/apollo/documents/CreateDocument.gql";

  import UIButton from "@/components/button/ui-button.vue";
  import UploadIcon from '@/components/icons/UploadIcon.vue';
  import { User } from "@/generated/graphql"

  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import splitFileNameExt from "@/utils/splitFileNameExt";
  import { FileSizeRestrictions } from "@/entities/FileSizeRestrictions";
  import DocumentProjectAccessScope from "@/data/documentAccessScope/DocumentProjectAccessScope";
  import UploadedFileData from "@/entities/file/UploadedFileData";
  import USER_DATA from "@/apollo/user/GetUserBasicInfo.gql"

  interface HTMLInputEvent extends Event {
    target: HTMLInputElement & EventTarget;
  }

  @Component({
    components: {
      UIButton,
      UploadIcon
    },
  })
  export default class UploadDocument extends Vue {
    @Prop({ type: String, required: true }) readonly currentUserId!: string;
    @Prop({ type: String, default: undefined }) readonly attachedToUserId?: string;
    @Prop({ type: String, default: undefined }) readonly companyId?: string;
    // @Prop({ type: Object, required: true }) readonly currentUser!: User;
    @Prop({ type: String, default: undefined }) readonly projectId?: string;
    @Prop({ type: String, default: undefined }) readonly groupId?: string;
    @Prop({ type: String, default: undefined }) readonly buttonSize?: string;
    @Prop({ type: Boolean, default: false }) readonly preventAutoUploading!: boolean;
    @Prop({ type: String, default: undefined }) readonly customRequestId?: string;

    processing: boolean = false;
    file: File | null = null;
    userProfile?: User[] = undefined;
    currentUser?: User = undefined;



    created(): void {
      this.getUserProfile();
    }

    get additionalData(): Object {
      const projects = {
        data: {
          project_id: this.projectId,
          access_scope: DocumentProjectAccessScope.MANAGEMENT,
        },
      };
      const custom_requests = {
        data: {
          custom_request_id: this.customRequestId,
          created_by: this.currentUserId,
        }
      }
      const data = {
        company_id: this.companyId,
        team_id: this.groupId,
        document_projects: this.projectId ? projects : undefined,
        document_custom_requests: this.customRequestId ? custom_requests : undefined,
      };

      if (!this.attachedToUserId) {
        return data;
      }

      const attachedTo = {
        attached_to: {
          data: {
            user_id: this.attachedToUserId,
          },
        },
      };
      return { ...data, ...attachedTo };
    }

    get fileInputElement(): HTMLInputElement {
      return this.$refs.fileInput as HTMLInputElement;
    }

    @Watch("file", {immediate: true})
    onFileChange() {
      this.$emit("fileUpdated", this.file);

      if (!this.file) {
      this.file = null;
      return;
    }

    this.file = this.file;
    const filesize = this.file.size / 1024 / 1024;
    if (filesize >= FileSizeRestrictions.DOCUMENT_MAX) {
      return;
    }

      if (this.file && !this.preventAutoUploading) {
        this.upload();
      }
    }

    addFile(): void {
      this.fileInputElement.click();
    }

    removeFile() {
      this.file = null;
      this.clearFileInputValue();
    }

    clearFileInputValue() {
      this.fileInputElement.value = "";
    }

async onDropFiles(event){
      if (!event.dataTransfer) {
      return;
    }
        await this.onUploadFile(event.dataTransfer?.files[0]);
}
    async fileChange(event: HTMLInputEvent): Promise<void> {
      const file = event.target.files?.[0];
      this.onUploadFile(file)

    }
    async onUploadFile(file){
     if (!file) {
        this.file = null;
        return;
      }

      this.file = file;
      const filesize = file.size / 1024 / 1024;
      if (filesize >= FileSizeRestrictions.DOCUMENT_MAX) {
        this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage(`The maximum allowed file size is ${FileSizeRestrictions.DOCUMENT_MAX} MB`)
        );
        return;
      }
    }

    async upload(): Promise<UploadedFileData> {
      const fileData = await this.createFile(this.file);
      this.$emit("documentAdded", this.file);
      this.clearFileInputValue();
      return fileData!;
    }

    async createFile(file: any) {
      this.processing = true;
      try {
        const s3UID = await this.uploadFile(file);

        const fileData: any = {
          id: s3UID,
          filename: file.name,
          format: file.type,
          size: file.size.toString(),
          status: "UPLOADED",
          uploaded_by: this.currentUserId,
          added_by: this.currentUser?.given_name + " " + this.currentUser?.family_name,
        };

        if (this.preventAutoUploading) {
          return fileData;
        }

        await this.saveDocumentInfo({
          ...fileData,
          ...this.additionalData,
        });


        this.$eventBus.emit(NOTIFICATION, Message.successMessage("File was uploaded"));
        this.$emit("refetchDocuments")
      } catch (e) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Something went wrong!"));
      } finally {
        this.processing = false;
      }
    }

    async uploadFile(file: any) {
      const uid = uuid();
      const { ext, name } = splitFileNameExt(file.name);
      const s3Filename = `${name}-${uid}.${ext}`;
      await this.saveDocument(s3Filename, file);
      return uid;
    }

    saveDocumentInfo(object: any) {
      return this.$apollo.mutate({
        mutation: CREATE_DOCUMENT,
        variables: { object },
      });
    }

    // async userNameGet(): Promise<void> {
    //   const result:any = this.$apollo.query({
    //     query:  USER_DATA,
    //     variables: {
    //       id: this.currentUserId
    //     },
    //   })

    //   // return result.data.user_by_pk.given_name + result.data.user_by_pk.family_name;
    //   this.userProfile = result;
    // }

    async getUserProfile(): Promise<void> {
      const result: any = await this.$apollo.query({
        query: USER_DATA,
        variables: {
          id: this.currentUserId
        },
      });
      this.currentUser = result.data.user_by_pk;
    }

    saveDocument(key: string, file: any) {
      return storage.save(key, file)
        .then((result: any) => result.key)
        .catch((err: any) => {
          throw err;
        });
    }

  }
